import { Component, ojs, app, payload, v } from "modular-openscriptjs";
import {
    formatDateForHumans,
    either,
    empty,
    isMobile,
    cleanDispose,
    calculateWaypointsDistance,
} from "../../helpers";

const h = app("h");
const broker = app("broker");
const $e = app("$e");
const gc = app("gc");
const dc = app("dc");
const api = app("api");
import { tool } from "../../fotastart";

export default class OrderOverview extends Component {
    render(order, onViewDetails, ...args) {
        const user = order?.user;
        const phone = order?.user?.phone || "";
        const formattedPhone = phone && phone[0] !== "+" ? "+" + phone.replace(/\D/g, '') : phone;
        const orderIdDisplay = "#" + (order?.id || "N/A");

        let totalItems = 0;
        let itemsPurchased = 0;
        if (order?.items) {
            totalItems = order.items.length;
            itemsPurchased = order.items.filter(i => i.purchased || i.status === 'ready' || i.status === 'delivered').length;
        }

        const createdDate = new Date(order?.created_at || Date.now());

        // Base time: prefer confirmed_at, fall back to created_at
        const confirmedAt = order?.confirmed_at
            ? new Date(order.confirmed_at)
            : createdDate;

        // Durations in ms (backend sends seconds; default 600s each)
        const processingTimeMs = (order?.processing_time ?? 600) * 1000;
        const deliveryTimeMs   = (order?.delivery_time   ?? 600) * 1000;

        // The single source of truth for "when is this order due"
        const dueAt  = new Date(confirmedAt.getTime() + processingTimeMs + deliveryTimeMs);
        const nowDate = new Date();

        // Progress bar: how far are we between confirmedAt → dueAt
        let totalMins   = Math.max(1, Math.round((dueAt - confirmedAt) / 60000));
        let elapsedMins = Math.max(0, Math.round((nowDate - confirmedAt) / 60000));
        const p   = elapsedMins / totalMins;
        const rag = p < 0.667 ? 'green' : p < 0.833 ? 'amber' : 'red';

        const formatDuration = (totalMins) => {
            const m = Math.max(0, totalMins);
            if (m < 60) return `${m}mins`;
            const hrs = Math.floor(m / 60);
            const mins = m % 60;
            if (hrs < 24) {
                return mins > 0 ? `${hrs}hr ${mins}mins` : `${hrs}hr`;
            }
            const days = Math.floor(hrs / 24);
            const remainingHours = hrs % 24;
            let parts = [`${days}d`];
            if (remainingHours > 0) parts.push(`${remainingHours}hr`);
            if (mins > 0) parts.push(`${mins}mins`);
            return parts.join(' ');
        };

        const formatCompletedDuration = (diffMs) => {
            const isLate = diffMs > 0;
            const absMs = Math.abs(diffMs);
            const absDiffMinutes = Math.round(absMs / 60000);
            
            let timeStr = "";
            if (absDiffMinutes < 60) {
                timeStr = `${absDiffMinutes}mins`;
            } else {
                const hours = Math.round(absDiffMinutes / 60);
                timeStr = hours === 1 ? `1hr` : `${hours}hrs`;
            }
            
            return `${timeStr} ${isLate ? 'late' : 'early'}`;
        };

        // Calculate due time label shown on the card
        let dueLabelText = "due";
        let dueValueText = "N/A";

        const RAG_CSS = {
            green: { bar: '#2e8018', lt: '#ecf7e8', tx: '#1e5e0e', bdr: '#c2e2b8' },
            amber: { bar: '#b87510', lt: '#fef5e4', tx: '#7a4d00', bdr: '#f0d090' },
            red:   { bar: '#c43030', lt: '#fef0f0', tx: '#8c1a1a', bdr: '#f0b8b8' },
        };
        const rc = RAG_CSS[rag];
        const isRed = rag === 'red';
        let dueColor = rc.bar;

        if (order?.status === "completed") {
            if (order?.completed_at) {
                let completedTime = new Date(order.completed_at).getTime();
                let diffMs = completedTime - dueAt.getTime();
                let isLate = diffMs > 0;

                dueLabelText = "finished";
                dueValueText = formatCompletedDuration(diffMs);
                dueColor = isLate ? "#c43030" : "#2e8018";
            } else {
                dueLabelText = "finished";
                dueValueText = "Delivered";
                dueColor = "#2e8018";
            }
        } else if (order?.status !== "cancelled") {
            let now = nowDate.getTime();
            let dueTime = dueAt.getTime();

            let diffMs = dueTime - now;
            let isLate = diffMs < 0;
            let absDiffMinutes = Math.round(Math.abs(diffMs) / 60000);

            dueLabelText = isLate ? "late" : "due";
            dueValueText = formatDuration(absDiffMinutes);
        }


        const fmtMins = (mins) => {
            const m = Math.max(0, mins);
            if (m === 0) return 'NOW';
            if (m < 60) return m + 'm';
            const h = Math.floor(m / 60), rm = m % 60;
            return rm ? `${h}h ${rm}m` : `${h}h`;
        };

        const placedTime = fmtMins(elapsedMins);

        const isDeliveryRequest = order?.type === "delivery_request" || order?.delivery_request_id;
        const isDelivering = order?.status === 'delivering';

        let pillBg, pillCol, pillBdr, pillTxt;
        if (isDelivering) {
            pillBg = '#e8f0fc'; pillCol = '#1a5faa'; pillBdr = '#b8d0f4'; pillTxt = 'Delivering';
        } else if (isDeliveryRequest) {
            const statusLabel = order?.status ? order.status.charAt(0).toUpperCase() + order.status.slice(1) : 'Pending';
            pillBg = '#f4f5f8'; pillCol = '#6b6e7a'; pillBdr = '#e2e4ea'; pillTxt = statusLabel;
        } else if (totalItems > 0 && itemsPurchased >= totalItems) {
            pillBg = '#ecf7e8'; pillCol = '#1e5e0e'; pillBdr = '#c2e2b8'; pillTxt = 'Ready to deliver';
        } else {
            pillBg = '#f4f5f8'; pillCol = '#6b6e7a'; pillBdr = '#e2e4ea'; pillTxt = `${itemsPurchased} / ${totalItems} items`;
        }

        const buildDots = () => {
            let dots = [];
            for (let i = 0; i < totalItems; i++) {
                const filled = i < itemsPurchased;
                const bg = filled ? (isDelivering ? '#3a7acc' : '#2e8018') : '#e4e6ee';
                const w = filled ? '18px' : '12px';
                dots.push(h.div({ style: `height: 4px; border-radius: 2px; transition: background .25s ease, width .25s ease; background:${bg}; width:${w};` }));
            }
            return h.div({ class: "d-flex gap-1 align-items-center" }, ...dots);
        };

        const customerName = user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() : "Unknown";
        const addrText = isDeliveryRequest
            ? "Dropoff Location"
            : (order?.delivery_address?.address?.replace(/\n/g, ' ') || "Unknown Address");
        const isNew = order?.status === 'confirmed';

        return h.div(
            { $class: "col-12 col-md-6 col-lg-4", class: "h-100 mb-3" },
            v(
                dc.orderItemsMapping,
                (orderItemsMapping) => {
                    const orderItems = orderItemsMapping.value[order.id];
                    const hasSelectedItems = orderItems && Object.keys(orderItems).length > 0;
                    const cardStyle = hasSelectedItems ? "border: 2px solid #198754 !important;" : "";

                    return h.div(
                        { class: "order-card-wrapper d-flex flex-column" },
                        h.div(
                            {
                                class: "order-card flex-grow-1",
                                style: cardStyle,
                                onclick: onViewDetails,
                            },
                            h.div({
                                class: `rag-strip ${isRed ? 'pulsing-rag' : ''}`,
                                style: `background: ${rc.bar};`,
                            }),
                            h.div(
                                { class: "card-inner" },
                                
                                // Row 1: meta left | due time right
                                h.div(
                                    { class: "d-flex align-items-center justify-content-between mb-1" },
                                    h.div(
                                        { class: "d-flex align-items-center gap-1" },
                                        h.span({ class: "order-id" }, `#${order?.order_code}`),
                                        isNew ? h.span({ class: "new-badge" }, "NEW") : null,
                                        h.span({ class: "sep" }, "·"),
                                        h.span({ class: "placed-time" }, `${placedTime} ago`)
                                    ),
                                    either(
                                        order?.status !== "cancelled",
                                        () => h.div(
                                            { class: "d-flex align-items-baseline gap-1" },
                                            h.span({ class: "due-label" }, dueLabelText),
                                            h.span({ class: "due-value", style: `color: ${dueColor};` }, dueValueText)
                                        )
                                    )
                                ),

                                // Row 2: item dots | status pill
                                h.div(
                                    { class: "d-flex align-items-center justify-content-between mb-2" },
                                    either(
                                        isDeliveryRequest,
                                        () => h.div(
                                            { class: "d-flex align-items-center gap-2 text-secondary fw-semibold", style: "font-size: 11px;" },
                                            h.i({ class: "bi bi-bicycle" }),
                                            "Pickup/Dropoff"
                                        ),
                                        () => buildDots()
                                    ),
                                    h.span({ class: "status-pill", style: `background:${pillBg}; color:${pillCol}; border: 0.5px solid ${pillBdr};` }, pillTxt)
                                ),

                                // Divider
                                h.hr({ class: "card-divider" }),

                                // Row 3: customer name | CTA buttons
                                h.div(
                                    { class: "d-flex align-items-center justify-content-between mb-1" },
                                    h.span({ class: "customer-name" }, customerName),
                                    h.div(
                                        { class: "d-flex gap-1" },
                                        either(phone, () => h.a({
                                            href: `tel:${formattedPhone}`,
                                            class: "cta-btn",
                                            aria_label: `Call ${customerName}`,
                                            onclick: "event.stopPropagation();"
                                        }, h.i({ class: "bi bi-telephone" }))),
                                        either(phone, () => h.a({
                                            href: `https://wa.me/${formattedPhone.replace('+', '')}?text=Hi, I am texting regarding order #${order?.order_code}`,
                                            class: "cta-btn",
                                            target: "_blank",
                                            rel: "noopener noreferrer",
                                            onclick: "event.stopPropagation();"
                                        }, h.i({ class: "bi bi-whatsapp" }))),
                                        either(order?.audio_url || order?.pivot?.audio_url || order?.audio_path, () => h.button({
                                            class: "cta-btn",
                                            listeners: {
                                                click: (e) => {
                                                    e.stopPropagation();
                                                    this.readAudio(order);
                                                }
                                            },
                                            title: "Listen to Order Audio"
                                        }, h.i({ class: "bi bi-volume-up-fill" })))
                                    )
                                ),

                                // Row 4: delivery address link
                                h.a(
                                    {
                                        href: "javascript:void(0)",
                                        class: "addr-link d-flex align-items-center gap-1",
                                        listeners: {
                                            click: (e) => {
                                                e.stopPropagation();
                                                this.openDeliveryAddressModal(order);
                                            }
                                        },
                                        aria_label: `Open delivery address for ${customerName}`
                                    },
                                    h.i({ class: "bi bi-geo-alt", style: "font-size:12px; flex-shrink:0;" }),
                                    h.span({ class: "addr-text" }, addrText),
                                    h.i({ class: "bi bi-box-arrow-up-right", style: "font-size:11px; flex-shrink:0;" })
                                ),

                                // Row 5: status transition links
                                either(
                                    !empty(order?.previous_status?.url) || !empty(order?.next_status?.url),
                                    () => h.div(
                                        { class: "d-flex justify-content-between align-items-center mt-2" },
                                        either(
                                            !empty(order?.previous_status?.url),
                                            () => h.a(
                                                {
                                                    href: "javascript:void(0)",
                                                    class: "addr-link text-secondary fw-semibold d-inline-flex align-items-center gap-1",
                                                    style: "font-size: 11px;",
                                                    listeners: {
                                                        click: (e) => {
                                                            e.stopPropagation();
                                                            this.changeStatus(order.id, order.previous_status.url, "previous");
                                                        }
                                                    }
                                                },
                                                h.i({ class: "bi bi-arrow-left" }),
                                                order.previous_status.name || "Back"
                                            )
                                        ),
                                        either(
                                            empty(order?.previous_status?.url),
                                            () => h.div()
                                        ),
                                        either(
                                            !empty(order?.next_status?.url),
                                            () => h.a(
                                                {
                                                    href: "javascript:void(0)",
                                                    class: "addr-link fw-semibold d-inline-flex align-items-center gap-1",
                                                    style: "font-size: 11px;",
                                                    listeners: {
                                                        click: (e) => {
                                                            e.stopPropagation();
                                                            this.changeStatus(order.id, order.next_status.url, "next");
                                                        }
                                                    }
                                                },
                                                order.next_status.name || "Next",
                                                h.i({ class: "bi bi-arrow-right" })
                                            )
                                        )
                                    )
                                )
                            )
                        )
                    );
                },
                { $class: "h-100", class: "h-100" }
            ),
            ...args
        );
    }

    async openDirections(order) {
        const isDeliveryRequest = order?.type === "delivery_request" || order?.delivery_request_id;

        let url = `https://www.google.com/maps/dir/${dc.latitude.value || dc.latitude},${dc.longitude.value || dc.longitude}/`;

        if (isDeliveryRequest) {
            const req = order?.delivery_request;
            const pickupLat = req?.pickup_latitude;
            const pickupLng = req?.pickup_longitude;
            const dropoffLat = req?.dropoff_latitude;
            const dropoffLng = req?.dropoff_longitude;

            if (pickupLat && pickupLng) {
                url += `${pickupLat},${pickupLng}/`;
            }
            if (dropoffLat && dropoffLng) {
                url += `${dropoffLat},${dropoffLng}/`;
            }
        } else {
            const address = order?.delivery_address;
            const latitude = address?.latitude;
            const longitude = address?.longitude;
            if (!latitude || !longitude) return;

            url += `${latitude},${longitude}/`;
        }

        let a = h.a({ href: url, target: "_blank" });
        a.click();
    }

    audioPlayerHolder = null;

    readAudio(order) {
        if (window.speechSynthesis) {
            window.speechSynthesis.cancel();
        }

        if (this.audioPlayerHolder) cleanDispose(this.audioPlayerHolder);

        const player = () => h.AudioPlayerView(order);

        if (isMobile()) {
            this.audioPlayerHolder = tool.offcanvas(h.OffcanvasBody(player()), {
                class: "rounded-top-5 shadow-lg",
                style: "height: auto; max-height: 95vh;",
            });
        } else {
            this.audioPlayerHolder = tool.plainModal(h.ModalBody(player()));
        }

        this.audioPlayerHolder.show();
    }

    openDeliveryAddressModal(order) {
        const address = order?.delivery_address;
        const isDeliveryRequest = order?.type === "delivery_request" || order?.delivery_request_id;

        let recipientName = "";
        let phone = "";

        if (isDeliveryRequest) {
            const req = order?.delivery_request;
            recipientName = req?.recipient_name ||
                (req?.first_name || req?.last_name ? `${req.first_name || ""} ${req.last_name || ""}`.trim() : "");
            phone = req?.recipient_number || req?.recipient_phone || req?.phone || "";

            // Fallback to customer info if delivery request doesn't have it
            if (!recipientName) {
                recipientName = address?.recipient_name ||
                    `${address?.first_name || ""} ${address?.last_name || ""}`.trim() ||
                    (order?.user ? `${order.user.first_name || ""} ${order.user.last_name || ""}`.trim() : "") ||
                    "Recipient";
            }
            if (!phone) {
                phone = address?.phone || order?.user?.phone || "";
            }
        } else {
            recipientName = address?.recipient_name ||
                `${address?.first_name || ""} ${address?.last_name || ""}`.trim() ||
                (order?.user ? `${order.user.first_name || ""} ${order.user.last_name || ""}`.trim() : "") ||
                "Recipient";
            phone = address?.phone || order?.user?.phone || "";
        }

        const formattedPhone = phone && phone[0] !== "+" ? "+" + phone.replace(/\D/g, '') : phone;

        let addressText = "";
        if (isDeliveryRequest) {
            const req = order?.delivery_request;
            const pickupName = req?.pickup_branch?.name || "Pickup Location";
            addressText = `Pickup: ${pickupName} → Dropoff: Recipient Location`;
        } else {
            addressText = address?.address || "Unknown Address";
        }

        let deliveryModal = null;

        deliveryModal = tool.plainModal(
            h.div(
                { class: "modal-content border-0 rounded-4 p-4 shadow-lg bg-white" },
                h.div(
                    { class: "modal-header border-0 pb-0 justify-content-between align-items-center" },
                    h.h5(
                        { class: "modal-title fw-bold text-dark fs-5 d-flex align-items-center gap-2" },
                        h.i({ class: "bi bi-geo-alt-fill text-danger" }),
                        "Delivery Address"
                    ),
                    h.button({
                        type: "button",
                        class: "btn-close shadow-none",
                        data_bs_dismiss: "modal",
                        aria_label: "Close",
                    })
                ),
                h.div(
                    { class: "modal-body pt-3" },
                    
                    // Recipient Section
                    h.div(
                        { class: "mb-4" },
                        h.small({ class: "text-muted text-uppercase fw-bold d-block mb-1", style: "font-size: 10px; letter-spacing: 0.05em;" }, "Recipient"),
                        h.div({ class: "fw-bold text-dark fs-5" }, recipientName)
                    ),

                    // Quick Action Buttons (Call & WhatsApp)
                    either(phone, () => h.div(
                        { class: "d-flex gap-3 mb-4" },
                        h.a(
                            {
                                href: `tel:${formattedPhone}`,
                                class: "btn btn-light border d-flex align-items-center justify-content-center gap-2 rounded-3 py-2.5 flex-grow-1 text-decoration-none text-dark fw-semibold fs-sm transition-all hover-light",
                                style: "background-color: var(--btn-bg, #f2f3f6); border-color: var(--btn-bdr, #dcdfe8);",
                                onclick: "event.stopPropagation();"
                            },
                            h.i({ class: "bi bi-telephone-fill text-primary" }),
                            "Call Recipient"
                        ),
                        h.a(
                            {
                                href: `https://wa.me/${formattedPhone.replace('+', '')}?text=Hi, I am texting regarding order #${order?.order_code || order?.id}`,
                                class: "btn btn-light border d-flex align-items-center justify-content-center gap-2 rounded-3 py-2.5 flex-grow-1 text-decoration-none text-dark fw-semibold fs-sm transition-all hover-light",
                                style: "background-color: var(--btn-bg, #f2f3f6); border-color: var(--btn-bdr, #dcdfe8);",
                                target: "_blank",
                                rel: "noopener noreferrer",
                                onclick: "event.stopPropagation();"
                            },
                            h.i({ class: "bi bi-whatsapp text-success" }),
                            "WhatsApp"
                        )
                    )),

                    // Location / Address Details
                    h.div(
                        { class: "mb-4" },
                        h.small({ class: "text-muted text-uppercase fw-bold d-block mb-2", style: "font-size: 10px; letter-spacing: 0.05em;" }, "Delivery Location"),
                        h.p({ class: "text-dark mb-0 fs-sm fw-medium lh-base text-wrap" }, addressText)
                    ),

                    // Order Notes / Additional Information
                    either(address?.additional_information, () =>
                        h.div(
                            {
                                class: "mb-4 p-3 bg-warning-subtle text-warning-emphasis rounded-3 border border-warning-subtle",
                            },
                            h.small(
                                { class: "fw-bold fs-xsm text-uppercase d-block mb-1", style: "font-size: 10px; letter-spacing: 0.05em;" },
                                "Order Notes / Instructions"
                            ),
                            h.p(
                                { class: "mb-0 fs-sm text-wrap" },
                                address.additional_information
                            )
                        )
                    ),

                    // Open in Google Maps Button
                    h.button(
                        {
                            class: "btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2 rounded-3 py-2.5 fw-semibold border-0",
                            style: "background: linear-gradient(135deg, #0d6efd, #0a58ca); box-shadow: 0 4px 12px rgba(13, 110, 253, 0.15);",
                            listeners: {
                                click: () => {
                                    deliveryModal?.hide();
                                    setTimeout(() => {
                                        cleanDispose(deliveryModal);
                                    }, 500);
                                    this.openDirections(order);
                                }
                            }
                        },
                        h.i({ class: "bi bi-map-fill" }),
                        "Open in Google Maps"
                    )
                )
            )
        );

        deliveryModal.show();
    }

    changeStatus(orderId, url, direction) {
        broker.send(
            $e.order.needs.statusChange,
            payload({
                orderId: orderId,
                url: url,
                direction: direction,
            }),
        );
    }
}

ojs(OrderOverview);
